import { useState, useEffect, useCallback } from "react";
import { getCookie, setCookie } from "cookies-next";
import { SetCookieFn, UseCookieListenerOptions } from "./props";

export function useCookieListener(
  name: string,
  options: UseCookieListenerOptions = { listen: false, intervalMs: 1000 }
): {
  cookieValue: string | undefined;
  setCookieValue: SetCookieFn;
} {
  const { listen = false, intervalMs = 1000 } = options;

  const [cookieValue, setCookieValueState] = useState<string | undefined>(
    () => {
      const initial = getCookie(name);
      return typeof initial === "string" ? initial : undefined;
    }
  );

  const readCookie = useCallback(() => {
    const value = getCookie(name);
    if (typeof value === "string") {
      setCookieValueState(value);
    } else {
      setCookieValueState(undefined);
    }
  }, [name]);

  useEffect(() => {
    if (listen) {
      const timer = setInterval(readCookie, intervalMs);
      return () => clearInterval(timer);
    }
  }, [listen, intervalMs, readCookie]);

  const setCookieValue: SetCookieFn = useCallback(
    (value, opts) => {
      setCookie(name, value, { path: opts?.path ?? "/", maxAge: opts?.maxAge });
      setCookieValueState(String(value));
    },
    [name]
  );

  return { cookieValue, setCookieValue };
}
